"use client";

import { useSession } from "next-auth/react";
import { useRouter, usePathname, useParams } from "next/navigation";
import { useEffect, useRef } from "react";
import Link from "next/link";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { loginUrl } from "@/lib/pwa-refresh";
import { TripDataProvider, useTripData } from "@/lib/trip-data-context";
import { timeAgo } from "@/lib/format";
import { FullPageSpinner, OfflineBanner } from "@/components/trip/ui";

function SyncBar() {
  const { lastSynced, syncing, sync, update, appUpdateAvailable } = useTripData();
  const isOnline = useOnlineStatus();

  // Data refresh and app update are separate actions: Sync pulls only this
  // trip's changed data (incremental, in place); Update pulls the newly
  // deployed app (SW update + reload). Nothing checks in the background —
  // a newer build is only discovered when a manual Sync's manifest carries
  // a different build hash, at which point the bar flips to Update.
  if (appUpdateAvailable) {
    return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, fontFamily: "var(--mono)", marginBottom: 20, color: "var(--warn)" }}>
        <span>✦ App update available</span>
        <button
          onClick={update}
          disabled={syncing}
          style={{
            background: "none",
            border: "1px solid var(--warn)",
            borderRadius: 4,
            padding: "2px 8px",
            fontSize: 11,
            fontFamily: "var(--mono)",
            color: syncing ? "var(--text4)" : "var(--warn)",
            cursor: syncing ? "default" : "pointer",
            fontWeight: 600,
          }}
        >
          {syncing ? "Updating..." : "Update ↺"}
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, fontFamily: "var(--mono)", color: "var(--text4)", marginBottom: 20 }}>
      <span>
        {!isOnline && "⚠ Offline · "}
        {lastSynced ? `Synced ${timeAgo(lastSynced)}` : "Not synced"}
      </span>
      <button
        onClick={sync}
        disabled={syncing || !isOnline}
        style={{
          background: "none",
          border: "1px solid var(--border)",
          borderRadius: 4,
          padding: "2px 8px",
          fontSize: 11,
          fontFamily: "var(--mono)",
          color: syncing || !isOnline ? "var(--text4)" : "var(--text3)",
          cursor: syncing ? "default" : "pointer",
        }}
      >
        {syncing ? "Syncing..." : "Sync Now"}
      </button>
    </div>
  );
}

function TripLayoutInner({ children }: { children: React.ReactNode }) {
  const { status } = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const isOnline = useOnlineStatus();
  const { data, loading } = useTripData();
  const tabBarRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLAnchorElement>(null);

  const tripId = String(params.id);
  const tabs = [
    { href: `/trip/${tripId}`, label: "★ Highlights" },
    { href: `/trip/${tripId}/itinerary`, label: "📍 Day-by-Day" },
    { href: `/trip/${tripId}/transit`, label: "✈ Transit" },
    { href: `/trip/${tripId}/hotels`, label: "🏠 Hotels" },
    { href: `/trip/${tripId}/budget`, label: "💰 Budget" },
  ];

  // Only redirect to login if: online AND confirmed unauthenticated AND the
  // data load has fully settled with nothing cached. Never redirect while
  // the cache read is still in flight — a fast session error must not bounce
  // a user who has cached content.
  useEffect(() => {
    if (status === "unauthenticated" && isOnline && !data && !loading) {
      router.push(loginUrl(pathname));
    }
  }, [status, isOnline, data, loading, router, pathname]);

  // Keep the active tab visible when the route changes AND when the tab bar
  // first mounts (data load finishing). On a full-page navigation (e.g.
  // offline fallback), this effect used to fire while the loading spinner
  // was up — no tab bar yet, ref null — and never again, leaving the active
  // tab scrolled out of view. Only scroll when the tab is actually clipped:
  // a tapped tab is visible by definition, and yanking the bar to re-center
  // it discards the user's own scroll position (online or offline).
  useEffect(() => {
    const bar = tabBarRef.current;
    const tab = activeTabRef.current;
    if (!bar || !tab) return;
    const barRect = bar.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const clipped = tabRect.left < barRect.left - 1 || tabRect.right > barRect.right + 1;
    if (clipped) tab.scrollIntoView({ inline: "center", block: "nearest" });
  }, [pathname, data, loading]);

  // Loading finished but there's nothing to show — offline (or server
  // unreachable) with no cached copy of this trip. Say so instead of
  // spinning forever.
  if (!data && !loading) {
    if (status === "unauthenticated" && isOnline) {
      return null;
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", gap: 14, padding: "0 24px", textAlign: "center" }}>
        <div style={{ fontSize: 28 }}>⚠</div>
        <div style={{ fontSize: 15, fontWeight: 700 }}>
          {isOnline ? "Couldn't load this trip" : "This trip isn't cached for offline yet"}
        </div>
        <p style={{ fontSize: 13, color: "var(--text3)", lineHeight: 1.6, maxWidth: 340, margin: 0 }}>
          {isOnline
            ? "The server couldn't be reached. Check your connection and try again."
            : "Open this trip once while online and it will be saved for offline use automatically."}
        </p>
        <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
          <button
            onClick={() => window.location.reload()}
            style={{ background: "none", border: "1px solid var(--border)", borderRadius: 4, padding: "6px 14px", fontSize: 12, fontFamily: "var(--mono)", color: "var(--text2)", cursor: "pointer" }}
          >
            Retry ↺
          </button>
          <Link
            href="/trips"
            style={{ display: "inline-flex", alignItems: "center", border: "1px solid var(--border)", borderRadius: 4, padding: "6px 14px", fontSize: 12, fontFamily: "var(--mono)", color: "var(--text2)", textDecoration: "none" }}
          >
            ← All trips
          </Link>
        </div>
      </div>
    );
  }

  // Show spinner until data + images are ready
  // If we have data and loading is done, render immediately
  if (!data || loading) {
    if (status === "unauthenticated" && isOnline && !loading) {
      // Redirect is happening via useEffect
      return null;
    }

    const statusText = "Loading trip data...";

    return <FullPageSpinner label={statusText} />;
  }

  const trip = data.trip;

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "32px 20px 80px" }}>
      {!isOnline && <OfflineBanner />}

      {/* App header — mirrors the Finances chrome: app identity on the left
          (tap → all trips), a home icon on the right that returns to the
          launcher, matching where "All apps" lives across the service. */}
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: 14,
          marginBottom: 22,
          borderBottom: "1px solid var(--border)",
        }}
      >
        <Link
          href="/trips"
          aria-label="All trips"
          style={{ display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none" }}
        >
          <span style={{ fontSize: 22, lineHeight: 1 }} aria-hidden>
            🧳
          </span>
          <span style={{ fontSize: 16, fontWeight: 700, color: "var(--text)", letterSpacing: "-0.01em" }}>
            Vacations
          </span>
        </Link>
        <Link
          href="/"
          aria-label="All apps"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 34,
            height: 34,
            borderRadius: 999,
            color: "var(--text3)",
            textDecoration: "none",
          }}
        >
          <svg
            viewBox="0 0 24 24"
            width={20}
            height={20}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M3 12l9-8 9 8M5 10v10h5v-6h4v6h5V10" />
          </svg>
        </Link>
      </header>

      <div style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
        {trip.dateLabel}
      </div>
      <h1 style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.2, margin: "0 0 6px" }}>
        {trip.title}
      </h1>
      <p style={{ fontSize: 13, color: "var(--text-warm)", margin: "0 0 4px", lineHeight: 1.5 }}>
        {trip.routeLine}
      </p>
      <p style={{ fontSize: 12, color: "var(--text3)", fontFamily: "var(--mono)", margin: 0 }}>
        {trip.summaryLine}
      </p>

      <div ref={tabBarRef} style={{ display: "flex", borderBottom: "2px solid var(--border)", margin: "28px 0 0", overflowX: "auto", overflowY: "hidden", WebkitOverflowScrolling: "touch", touchAction: "pan-x" }} className="scrollbar-hide">
        {tabs.map((tab) => {
          const active = pathname === tab.href;
          return (
            <Link
              key={tab.href}
              href={tab.href}
              ref={active ? activeTabRef : undefined}
              style={{
                background: "none",
                border: "none",
                padding: "10px 12px",
                fontSize: 11,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                cursor: "pointer",
                color: active ? "var(--text)" : "var(--text3)",
                borderBottom: active ? "2px solid var(--text)" : "2px solid transparent",
                marginBottom: -2,
                fontFamily: "var(--mono)",
                whiteSpace: "nowrap",
                textDecoration: "none",
                transition: "color 0.15s",
              }}
            >
              {tab.label}
            </Link>
          );
        })}
      </div>

      <div style={{ marginTop: 20 }}>
        <SyncBar />
        {children}
      </div>
    </div>
  );
}

export default function TripChrome({ children }: { children: React.ReactNode }) {
  const params = useParams();
  const tripId = String(params.id);
  return (
    <TripDataProvider tripId={tripId}>
      <TripLayoutInner>{children}</TripLayoutInner>
    </TripDataProvider>
  );
}
